import React, {
  FC,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocalStorage } from "usehooks-ts";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Box,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
  alpha,
  Tooltip,
  TextField,
  Button,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";

import { DMAnswersExportButton } from "./DMAnswersExportButton";
import { DMAggregatedResultsExportButton } from "./DMAggregatedResultsExportButton";
import { AggregatedResultsTable } from "./components/AggregatedResultsTable";
import { StakeholdersResultsTable } from "./components/StakeholdersResultsTable";
import { CampaignStatusCard } from "./components/CampaignStatusCard";

import { useGetCurrentCompanyInfo, useGetStakeholders } from "@api";
import {
  QuestionAnswerRow,
  useGetQuestionAnswers,
} from "@api/hooks/doubleMateriality/GetQuestionAnswers/useGetQuestionAnswers";
import {
  useGetResults,
  useGetConsolidatedResults,
  DoubleMaterialityResultRow,
  DoubleMaterialityConsolidatedResultRow,
  useGetSurveys,
} from "@api/hooks/doubleMateriality";
import { useUserStore } from "@store/user";
import { getYearFromSearchParams } from "@helpers";

const DMSurveyResults: FC = () => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { enqueueSnackbar } = useSnackbar();

  const [searchParams] = useSearchParams();
  const year = getYearFromSearchParams(searchParams);

  const { stakeholders } = useGetStakeholders(user, {
    pageNumber: 1,
    pageSize: 1000,
    year,
  });
  const { answers } = useGetQuestionAnswers(user);
  const { surveys } = useGetSurveys(user);
  const { results, isPendingResults } = useGetResults(user, { year });
  const { currentCompanyInfo } = useGetCurrentCompanyInfo(user);
  const hasChildCompanies = currentCompanyInfo?.hasChildCompanies ?? false;

  const currentSurvey = surveys?.find((s) => s.year === year);
  const campaignActive = currentSurvey?.isActive || false;

  type MaterialityRow =
    | DoubleMaterialityResultRow
    | DoubleMaterialityConsolidatedResultRow;

  const [thresholds, setThresholds] = useLocalStorage("dm-results-thresholds", {
    externalImpact: 2.5,
    internalFinancial: 3,
  });
  const [thresholdsChanged, setThresholdsChanged] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [isConsolidatedView, setIsConsolidatedView] = useState(false);
  const externalThresholdInputRef = useRef<HTMLInputElement | null>(null);
  const internalThresholdInputRef = useRef<HTMLInputElement | null>(null);
  const justSavedRef = useRef(false);

  const formatThresholdInput = (value: number): string =>
    value.toString().replace(".", ",");

  const {
    consolidatedResults,
    isPendingConsolidatedResults,
    refetchConsolidatedResults,
  } = useGetConsolidatedResults(
    user,
    {
      year,
      impactThreshold: thresholds.externalImpact,
      financialThreshold: thresholds.internalFinancial,
    },
    { enabled: hasChildCompanies && isConsolidatedView },
  );

  const refreshConsolidatedResultsIfNeeded = useCallback(() => {
    if (isConsolidatedView) {
      refetchConsolidatedResults();
    }
  }, [isConsolidatedView, refetchConsolidatedResults]);

  useEffect(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;

      return;
    }
    setThresholdsChanged(false);
    setShowSaveSuccess(false);
  }, [thresholds.externalImpact, thresholds.internalFinancial]);

  useEffect(() => {
    if (!hasChildCompanies) {
      setIsConsolidatedView(false);
    }
  }, [hasChildCompanies]);

  useEffect(() => {
    refreshConsolidatedResultsIfNeeded();
  }, [refreshConsolidatedResultsIfNeeded]);

  const stakeholdersList = stakeholders?.data ?? [];
  const answersList = useMemo(
    () => (answers ?? []) as QuestionAnswerRow[],
    [answers],
  );
  const baseResultRows = (results ?? []) as DoubleMaterialityResultRow[];
  const consolidatedResultRows = (consolidatedResults ??
    []) as DoubleMaterialityConsolidatedResultRow[];
  const tableRows: MaterialityRow[] = isConsolidatedView
    ? consolidatedResultRows
    : baseResultRows;
  const isTableLoading = isConsolidatedView
    ? isPendingConsolidatedResults
    : isPendingResults;

  const parseThresholdValue = (value: string): number | null => {
    const normalized = value.replace(",", ".").trim();

    if (!normalized) return null;
    const parsed = Number(normalized);

    if (Number.isNaN(parsed)) return null;

    return parsed;
  };

  const parseThresholdInputs = (
    externalValue: string,
    internalValue: string,
  ): { external: number | null; internal: number | null } => ({
    external: parseThresholdValue(externalValue),
    internal: parseThresholdValue(internalValue),
  });

  const getThresholdInputValues = (
    key?: "externalImpact" | "internalFinancial",
    overrideValue?: string,
  ): { external: string; internal: string } => {
    const external =
      key === "externalImpact"
        ? (overrideValue ?? "")
        : (externalThresholdInputRef.current?.value ??
          formatThresholdInput(thresholds.externalImpact));
    const internal =
      key === "internalFinancial"
        ? (overrideValue ?? "")
        : (internalThresholdInputRef.current?.value ??
          formatThresholdInput(thresholds.internalFinancial));

    return { external, internal };
  };

  const syncInputWithValue = (
    ref: MutableRefObject<HTMLInputElement | null>,
    value: number,
  ): void => {
    if (ref.current) {
      ref.current.value = formatThresholdInput(value);
    }
  };

  const handleThresholdInputChange = (
    key: "externalImpact" | "internalFinancial",
    value: string,
  ): void => {
    const { external, internal } = getThresholdInputValues(key, value);
    const { external: externalParsed, internal: internalParsed } =
      parseThresholdInputs(external, internal);

    const hasInvalidValues = externalParsed == null || internalParsed == null;
    const hasChanges =
      hasInvalidValues ||
      externalParsed !== thresholds.externalImpact ||
      internalParsed !== thresholds.internalFinancial;

    setThresholdsChanged(hasChanges);
    setShowSaveSuccess(false);
    refreshConsolidatedResultsIfNeeded();
  };

  const handleThresholdSave = (): void => {
    const { external: externalRaw, internal: internalRaw } =
      getThresholdInputValues();
    const { external, internal } = parseThresholdInputs(externalRaw, internalRaw);

    if (external == null || internal == null) {
      enqueueSnackbar(
        t(
          "doubleMateriality.results.thresholdInvalid",
          "Enter thresholds between 0 and 5.",
        ),
        { variant: "error" },
      );

      return;
    }

    const clampedExternal = Math.min(Math.max(external, 0), 5);
    const clampedInternal = Math.min(Math.max(internal, 0), 5);

    justSavedRef.current = true;
    setThresholds({
      externalImpact: clampedExternal,
      internalFinancial: clampedInternal,
    });
    syncInputWithValue(externalThresholdInputRef, clampedExternal);
    syncInputWithValue(internalThresholdInputRef, clampedInternal);
    setThresholdsChanged(false);
    setShowSaveSuccess(true);
    refreshConsolidatedResultsIfNeeded();
  };

  const { stakeholdersWithAssignedQuestions, answeringRate } = useMemo(() => {
    const stakeholderStats = new Map<string, { total: number; answered: number }>();

    answersList.forEach((answer) => {
      const stakeholderId = answer.stakeholderId;

      if (!stakeholderId) return;

      const stats = stakeholderStats.get(stakeholderId) ?? {
        total: 0,
        answered: 0,
      };

      stats.total += 1;
      if (answer.answeredAt) stats.answered += 1;
      stakeholderStats.set(stakeholderId, stats);
    });

    const assignedStakeholders = stakeholdersList.filter((stakeholder) =>
      stakeholderStats.has(stakeholder.id),
    );
    const completedCount = assignedStakeholders.filter((stakeholder) => {
      const stats = stakeholderStats.get(stakeholder.id);

      return stats ? stats.answered === stats.total : false;
    }).length;
    const totalCount = assignedStakeholders.length;
    const rate = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

    return {
      stakeholdersWithAssignedQuestions: assignedStakeholders,
      answeringRate: rate,
    };
  }, [answersList, stakeholdersList]);

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
          borderRadius: 3,
          px: { xs: 2.5, sm: 3 },
          py: { xs: 2.5, sm: 3 },
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {t("doubleMateriality.results.thresholdTitle", "Set threshold")}

        <Typography variant="body2" color="text.secondary">
          {t(
            "doubleMateriality.results.thresholdSubtitle",
            "Choose thresholds between 0 and 5 for impact and financial materiality.",
          )}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 2, sm: 3 }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="flex-start"
        >
          <TextField
            key={`external-${thresholds.externalImpact}`}
            variant="outlined"
            size="small"
            label={t(
              "doubleMateriality.results.externalThreshold",
              "External (Impact)",
            )}
            defaultValue={formatThresholdInput(thresholds.externalImpact)}
            inputRef={externalThresholdInputRef}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleThresholdInputChange("externalImpact", event.target.value)
            }
            inputProps={{ inputMode: "decimal" }}
          />
          <TextField
            key={`internal-${thresholds.internalFinancial}`}
            variant="outlined"
            size="small"
            label={t(
              "doubleMateriality.results.internalThreshold",
              "Internal (Financial)",
            )}
            defaultValue={formatThresholdInput(thresholds.internalFinancial)}
            inputRef={internalThresholdInputRef}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleThresholdInputChange("internalFinancial", event.target.value)
            }
            inputProps={{ inputMode: "decimal" }}
          />
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            {showSaveSuccess ? (
              <Typography
                variant="body2"
                sx={{
                  color: "success.main",
                  fontWeight: 600,
                  minHeight: 50,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {t("common.saved", "Saved")}
              </Typography>
            ) : thresholdsChanged ? (
              <Button
                variant="contained"
                size="small"
                onClick={handleThresholdSave}
                sx={{
                  borderRadius: 1,
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#1F1F1F",
                  minHeight: 50,
                  "&:hover": { backgroundColor: "#1F1F1F" },
                }}
              >
                {t("common.save", "Save")}
              </Button>
            ) : null}
          </Box>
        </Stack>
      </Box>
      <Box>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h6">
            {t("doubleMateriality.results.title", "Results")}
          </Typography>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="flex-end"
            flexWrap="wrap"
            sx={{ width: { xs: "100%", md: "auto" } }}
          >
            {hasChildCompanies && (
              <FormControlLabel
                control={
                  <Switch
                    color="info"
                    checked={isConsolidatedView}
                    onChange={(event) => setIsConsolidatedView(event.target.checked)}
                  />
                }
                label={t(
                  "doubleMateriality.results.consolidatedView",
                  "Consolidated view",
                )}
                labelPlacement="start"
                sx={{
                  color: (theme) => theme.palette.primary.main,
                  fontWeight: 600,
                  fontSize: 14,
                  "& .MuiFormControlLabel-label": {
                    color: (theme) => theme.palette.primary.main,
                    fontWeight: 600,
                    mr: 1,
                    fontSize: 16,
                  },
                }}
              />
            )}
            <DMAggregatedResultsExportButton
              tableRows={tableRows}
              answers={answersList}
              isTableLoading={isTableLoading}
              isConsolidatedView={isConsolidatedView}
              thresholds={thresholds}
            />
          </Stack>
        </Stack>
        <AggregatedResultsTable
          tableRows={tableRows}
          isTableLoading={isTableLoading}
          isConsolidatedView={isConsolidatedView}
          thresholds={thresholds}
        />
      </Box>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h6" gutterBottom>
          {t("doubleMateriality.dataCollectionStatus", "Data collection status")}
        </Typography>
        <Tooltip
          title={t(
            "doubleMateriality.dataCollectionStatusInfo",
            "Track the notification campaign status and overall answering rate for stakeholders.",
          )}
          arrow
        >
          <InfoOutlined color="action" fontSize="small" sx={{ mt: "2px" }} />
        </Tooltip>
      </Stack>

      {/* Campaign Status Section */}
      <CampaignStatusCard />

      {campaignActive && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t("doubleMateriality.answeringRate", "Answering rate")}
          </Typography>
          <Box
            sx={{
              width: "100%",
              bgcolor: "grey.200",
              borderRadius: 1,
              overflow: "hidden",
              position: "relative",
              height: 20,
            }}
          >
            <Box
              sx={{
                width: `${answeringRate}%`,
                bgcolor: (theme) => theme.palette.custom.teal[200],
                height: "100%",
                borderRadius: 1,
                transition: "width 0.3s ease-in-out",
              }}
            />
            <Typography
              variant="body2"
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                color: "text.primary",
                alignItems: "center",
                justifyContent: "center",
                textShadow: "0 0 4px rgba(0, 0, 0, 0.4)",
              }}
            >
              {answeringRate.toFixed(0)}%
            </Typography>
          </Box>
        </Box>
      )}

      {/* Divider */}
      <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
        <Box sx={{ width: "80%", height: "1px", bgcolor: "divider" }} />
      </Box>

      {/* Export Button */}
      <DMAnswersExportButton
        stakeholders={stakeholdersWithAssignedQuestions}
        answers={answersList}
        year={year}
      />

      {/* Results Table */}
      <StakeholdersResultsTable />
    </Stack>
  );
};

export { DMSurveyResults as DoubleMaterialityResults };